import { memo } from 'react';
import { Circle, G, Path, Text } from 'react-native-svg';
import { plotY } from './ComboAxes';
import { ComboLayout } from './layout';
import { Point, areaPath, runPath, segmentsOf } from './paths';
import { ComboLineStyle, ComboSeries, ComboTheme } from './types';

const kAreaOpacity = 0.18;

/**
 * Line and area series, drawn over the bars: areas first, then every
 * line, then their points and labels on top.
 */
export const ComboLines = memo(
  ({
    layout,
    series,
    line,
    dataLabels,
    theme,
  }: {
    layout: ComboLayout;
    series: ComboSeries[];
    line: ComboLineStyle;
    dataLabels: boolean;
    theme: ComboTheme;
  }) => {
    const drawn = series.filter(entry => entry.type !== 'bar');
    if (!drawn.length) return null;
    const { band, plot } = layout;
    const f = theme.fontSize;
    const traced = drawn.flatMap(entry => {
      const scale = entry.axis === 'right' ? layout.right : layout.left;
      if (!scale) return [];
      const points = entry.values.map((value, i): Point | undefined =>
        value === undefined
          ? undefined
          : { x: plot.left + band * (i + 0.5), y: plotY(layout, scale, value) },
      );
      const base = plotY(
        layout,
        scale,
        Math.min(Math.max(0, scale.min), scale.max),
      );
      return [{ entry, points, runs: segmentsOf(points), base }];
    });
    const showPoints = line.showPoints && band >= line.width * 3;
    return (
      <G>
        {traced.map(({ entry, runs, base }) =>
          entry.type === 'area'
            ? runs.map((run, r) => (
                <Path
                  key={`a-${entry.key}-${r}`}
                  d={areaPath(run, line.smooth, base)}
                  fill={entry.color}
                  fillOpacity={kAreaOpacity}
                />
              ))
            : null,
        )}
        {traced.map(({ entry, runs }) =>
          runs.map((run, r) => (
            <Path
              key={`l-${entry.key}-${r}`}
              d={runPath(run, line.smooth)}
              stroke={entry.color}
              strokeWidth={line.width}
              strokeLinejoin="round"
              strokeLinecap="round"
              fill="none"
            />
          )),
        )}
        {traced.map(({ entry, points }) =>
          points.map((point, i) => {
            // A lone point between gaps has no line: it always shows.
            const lone = !points[i - 1] && !points[i + 1];
            if (!point || !(showPoints || lone)) return null;
            return (
              <Circle
                key={`p-${entry.key}-${i}`}
                cx={point.x}
                cy={point.y}
                r={line.width + 1.5}
                fill={theme.background}
                stroke={entry.color}
                strokeWidth={Math.max(line.width, 1.5)}
              />
            );
          }),
        )}
        {dataLabels
          ? traced.map(({ entry, points }) =>
              points.map((point, i) => {
                const text = entry.labels?.[i];
                if (!point || !text || i % layout.step !== 0) return null;
                return (
                  <Text
                    key={`t-${entry.key}-${i}`}
                    x={point.x}
                    y={point.y - line.width - 5}
                    fontSize={f}
                    fontFamily={theme.fontFamily}
                    fill={theme.label}
                    textAnchor="middle"
                  >
                    {text}
                  </Text>
                );
              }),
            )
          : null}
      </G>
    );
  },
);
